// Configuration for the cashless "Remaining Due" screen buttons in posapp.
// Stored under cashless_configurations.remaining_due_buttons (JSONB), so this
// adds zero schema/Hasura churn. When the JSONB field is missing or partial,
// callers merge with these defaults — which match what posapp renders today,
// so existing events get unchanged behavior until someone edits this section.

export const REMAINING_DUE_BUTTON_IDS = [
  "physical_credit",
  "cashless_profile",
  "scan_additional",
] as const;

export type RemainingDueButtonId = (typeof REMAINING_DUE_BUTTON_IDS)[number];

// "exclude" hides the button on the Remaining Due screen entirely. It's a
// position value rather than a separate boolean so the admin UI can present
// all four states (Top / Middle / Bottom / Exclude) in a single dropdown.
export type RemainingDueButtonPosition = 1 | 2 | 3 | "exclude";

export type RemainingDueButton = {
  label: string;
  color: "red" | "black";
  position: RemainingDueButtonPosition;
};

export type RemainingDueButtons = Record<RemainingDueButtonId, RemainingDueButton>;

export const DEFAULT_REMAINING_DUE_BUTTONS: RemainingDueButtons = {
  physical_credit:  { label: "Pay with Physical Credit/Debit",                color: "red",   position: 1 },
  cashless_profile: { label: "Pay with Cashless Profile Linked Credit/Debit", color: "black", position: 2 },
  scan_additional:  { label: "Scan Additional QR/RFID",                       color: "black", position: 3 },
};

export const REMAINING_DUE_BUTTON_DISPLAY_NAMES: Record<RemainingDueButtonId, string> = {
  physical_credit:  "Physical Credit Button",
  cashless_profile: "Cashless Profile Button",
  scan_additional:  "Scan Additional Button",
};

// Merge a (possibly partial / missing) saved value with defaults so callers
// always get all three buttons fully populated.
export function mergeRemainingDueButtons(saved: any): RemainingDueButtons {
  const out = { ...DEFAULT_REMAINING_DUE_BUTTONS } as RemainingDueButtons;
  if (saved && typeof saved === "object") {
    for (const id of REMAINING_DUE_BUTTON_IDS) {
      if (saved[id] && typeof saved[id] === "object") {
        out[id] = { ...DEFAULT_REMAINING_DUE_BUTTONS[id], ...saved[id] };
      }
    }
  }
  return out;
}
